import { InformationCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { CircleX } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Container } from '@/components/legacy-ui';
import { getColumnWidth } from '@/components/table/columns/registry';
import { TanStackTable } from '@/components/table/TanStackTable';
import { Columns } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import type { Agent } from '@/hooks/useAgents';
import { formatDate } from '@/utils/date.util';

import { AddCredentialModal } from './AddCredentialModal';
import {
  ActiveDirectoryCredential,
  useActiveDirectoryCredentials,
} from './useActiveDirectoryCredentials';

interface CredentialsManagementTabProps {
  agent: Agent;
}

/**
 * Component for managing Active Directory credentials on Aegis agents
 *
 * Migrated from manual list to TanStackTable following UserManagementTab pattern.
 * Migration: 2025-12-07
 *
 * Provides interface for:
 * - Viewing current AD credentials on the agent
 * - Adding new credentials for authenticated scanning
 * - Removing existing credentials
 */
interface CredentialData {
  credentialId: string;
  name: string;
  created: string;
}

export const CredentialsManagementTab: React.FC<
  CredentialsManagementTabProps
> = ({ agent }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    credential: CredentialData | null;
  }>({ isOpen: false, credential: null });

  const { credentials, isLoading, addCredential, deleteCredential } =
    useActiveDirectoryCredentials(agent);

  const handleAddCredential = async (data: {
    label: string;
    username: string;
    password: string;
    domain: string;
  }) => {
    try {
      await addCredential.mutateAsync({
        ...data,
        resourceKey: agent.client_id,
      });
      // Close modal on success
      setIsAddModalOpen(false);
    } catch {
      // Error is handled by mutation's onError, but don't close modal
      // so user can see the error and try again
    }
  };

  const handleDeleteClick = (credential: CredentialData) => {
    setDeleteConfirmation({ isOpen: true, credential });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.credential) return;

    try {
      await deleteCredential.mutateAsync({
        credentialId: deleteConfirmation.credential.credentialId,
      });
    } finally {
      setDeleteConfirmation({ isOpen: false, credential: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, credential: null });
  };

  const credentialData: CredentialData[] = useMemo(
    () =>
      credentials.map((c: ActiveDirectoryCredential) => ({
        credentialId: c.credentialId,
        name: c.name,
        created: c.created,
      })),
    [credentials]
  );

  const columns: Columns<CredentialData> = useMemo(
    () => [
      {
        id: 'name',
        label: 'Credential',
        ...getColumnWidth('agentCredential', 'name'),
        cell: (data: CredentialData) => (
          <span className="text-chariot-text-primary font-medium">
            {data.name}
          </span>
        ),
      },
      {
        id: 'credentialId',
        label: 'ID',
        ...getColumnWidth('agentCredential', 'credentialId'),
        cell: (data: CredentialData) => (
          <CopyToClipboard textToCopy={data.credentialId} alwaysShowCopyIcon>
            <span className="text-chariot-text-secondary font-mono text-sm">
              {data.credentialId}
            </span>
          </CopyToClipboard>
        ),
      },
      {
        id: 'created',
        label: 'Created',
        ...getColumnWidth('agentCredential', 'created'),
        cell: (data: CredentialData) => (
          <span className="text-chariot-text-secondary text-sm">
            {formatDate(data.created)}
          </span>
        ),
      },
      {
        id: '',
        label: '',
        ...getColumnWidth('agentCredential', 'actions'),
        align: 'center',
        cell: (data: CredentialData) => (
          <Button
            variant="ghost"
            size="icon"
            data-testid={`delete-credential-${data.credentialId}-button`}
            onClick={() => handleDeleteClick(data)}
            disabled={deleteCredential.isPending}
            aria-label={`Delete credential ${data.name}`}
          >
            <CircleX className="fill-chariot-stroke-critical stroke-chariot-background-primary size-5" />
          </Button>
        ),
      },
    ],
    [deleteCredential.isPending]
  );

  if (!agent) return null;

  return (
    <div
      className="flex h-full flex-col gap-6"
      data-testid="credentials-management-tab"
    >
      {/* Single Container with Header + Table */}
      <Container
        className="border-chariot-stroke bg-chariot-background-primary flex h-full flex-1 flex-col rounded-md border p-6"
        data-testid="credentials-management-section"
      >
        {/* Header - ALWAYS visible (matches Tasks tab pattern) */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Active Directory Credentials
            </h3>
            <Tooltip title="Manage Active Directory credentials to enable authenticated scanning capabilities.">
              <div className="text-chariot-text-secondary cursor-help">
                <InformationCircleIcon className="size-4" />
              </div>
            </Tooltip>
          </div>
          {/* Only show Add button when credentials exist (empty state has its own button) */}
          {credentialData.length > 0 && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              size="sm"
              data-testid="add-credential-header-button"
            >
              Add Credential
            </Button>
          )}
        </div>

        {/* Credentials Table */}
        <div className="border-chariot-stroke flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
          <TanStackTable<CredentialData>
            name="agent-credentials"
            columns={columns}
            data={credentialData}
            status={isLoading ? 'pending' : 'success'}
            error={null}
            selection={{ hide: true }}
            noData={{
              title: 'No Credentials Configured',
              description:
                'Add Active Directory credentials to enable authenticated scanning capabilities.',
              icon: <KeyIcon />,
              action: (
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  size="sm"
                  data-testid="add-credential-empty-state-button"
                >
                  Add Credential
                </Button>
              ),
            }}
          />
        </div>
      </Container>

      {/* Add Credential Modal */}
      <AddCredentialModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddCredential}
        isSubmitting={addCredential.isPending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationDialog
        open={deleteConfirmation.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Credential"
        description={
          deleteConfirmation.credential
            ? `Are you sure you want to delete credential ${deleteConfirmation.credential.name}?`
            : ''
        }
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        isLoading={deleteCredential.isPending}
      />
    </div>
  );
};
